import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ShieldCheck, Check, MapPin, Monitor, Users, Wrench, Puzzle, Code2, Database } from "lucide-react";

function Stat({ icon: Icon, label, value, testid }) {
  return (
    <div className="rounded-xl border border-border p-3" data-testid={testid}>
      <p className="text-[11px] text-muted-foreground flex items-center gap-1.5"><Icon className="w-3.5 h-3.5" /> {label}</p>
      <p className="font-head font-extrabold text-lg mt-0.5">{value}</p>
    </div>
  );
}

function Policy({ icon: Icon, title, text, testid }) {
  return (
    <div className="flex gap-3" data-testid={testid}>
      <Icon className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
      <div><p className="text-sm font-semibold">{title}</p><p className="text-xs text-muted-foreground leading-relaxed">{text}</p></div>
    </div>
  );
}

export function ServiceDetailsContent({ data }) {
  const p = data.pricing, a = data.allowances, al = data.additional_location;
  return (
    <div className="space-y-5" data-testid="service-details-content">
      <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <p className="font-head font-bold">{p.plan} Plan</p>
          {a.custom && <Badge className="bg-sky-500/10 text-sky-600 border-sky-500/20 text-[10px]">Custom allowance</Badge>}
        </div>
        <div className="grid grid-cols-2 gap-3 mt-3">
          <div><p className="text-[11px] text-muted-foreground">One-time setup</p><p className="font-head font-extrabold text-xl" data-testid="sd-setup-fee">{p.setup_fee_label}</p></div>
          <div><p className="text-[11px] text-muted-foreground">Monthly</p><p className="font-head font-extrabold text-xl" data-testid="sd-monthly">{p.monthly_label}</p><p className="text-[11px] text-muted-foreground">{p.monthly_basis}{p.billable_locations > 1 ? ` · ${p.billable_locations} locations = ${p.recurring_total_label}/month` : ""}</p></div>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Stat icon={MapPin} label="Business location" value={`${a.locations} included`} testid="sd-locations" />
        <Stat icon={Monitor} label="POS registers / devices" value={`Up to ${a.registers_per_location} per location`} testid="sd-registers" />
        <Stat icon={Users} label="Staff accounts" value={`Owner + up to ${a.staff_per_location} per location`} testid="sd-staff" />
      </div>
      <div className="rounded-xl border border-border p-4" data-testid="sd-additional-location">
        <p className="text-sm font-semibold">Additional Location</p>
        <p className="text-xs text-muted-foreground">{al.text}</p>
        <p className="text-sm font-bold mt-1">{al.setup_fee_label} one-time setup + {al.monthly_label}/month per additional active location</p>
      </div>
      <div>
        <p className="text-sm font-semibold mb-2">Included in your plan</p>
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
          {data.features.map((f) => <li key={f} className="text-xs text-muted-foreground flex gap-2"><Check className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />{f}</li>)}
        </ul>
      </div>
      <div className="space-y-3">
        <Policy icon={Wrench} title="Hardware" text={data.policies.hardware} testid="sd-hardware" />
        <Policy icon={Puzzle} title="Custom work" text={data.policies.custom_work} testid="sd-custom-work" />
        <Policy icon={Code2} title="Software" text={data.policies.software_ownership} testid="sd-software" />
        <Policy icon={Database} title="Your business data" text={data.policies.data_ownership} testid="sd-data" />
      </div>
    </div>
  );
}

export default function ServiceDetailsGate() {
  const { loadBusiness } = useAuth();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/service-details").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e.response?.data?.detail))); }, []);

  const go = async () => {
    setSaving(true);
    try { await api.post("/service-details/continue"); await loadBusiness(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4" data-testid="service-details-gate">
      <div className="bg-background rounded-2xl border border-border w-full max-w-2xl max-h-[92vh] flex flex-col shadow-2xl">
        <div className="p-5 sm:p-6 border-b border-border">
          <p className="text-xs font-bold tracking-widest text-emerald-500 flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> NEEC POS</p>
          <h2 className="font-head text-2xl font-extrabold mt-1">Your Plan &amp; Service Details</h2>
          <p className="text-sm text-muted-foreground">A quick summary of your plan, what is included and how NEEC works with your business. You can review it any time under Settings.</p>
        </div>
        <div className="p-5 sm:p-6 overflow-y-auto">
          {data ? <ServiceDetailsContent data={data} /> : <p className="text-sm text-muted-foreground">Loading...</p>}
        </div>
        <div className="p-5 sm:p-6 border-t border-border flex items-center justify-end">
          <Button disabled={!data || saving} onClick={go} className="bg-emerald-600 hover:bg-emerald-700 text-white h-11 px-8 w-full sm:w-auto" data-testid="service-details-continue">
            {saving ? "Opening..." : "Continue to NEEC POS"}
          </Button>
        </div>
      </div>
    </div>
  );
}
